import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ListPlus, Mail } from 'lucide-react'
import Modal from '@/components/ui/Modal'
import { modalButton } from '@/components/ui/modalButton'
import Select from '@/components/ui/Select'
import { useAddToList, useLists } from '@/features/lists/useLists'
import { useDripSequences, useEnrollInDrip } from '@/features/drip/useDrip'
import { useDeleteContact } from '@/features/contacts/useContacts'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import type { Contact } from '@/api/types'

const nameOf = (c: Contact) => [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || 'this contact'

/** Who the dialog acts on, at the top — so it's clear which row's menu opened it. */
function ContactBadge({ contact }: { contact: Contact }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 mb-4">
      <div className="text-sm font-semibold text-brand-darkest">{nameOf(contact)}</div>
      {contact.email && <div className="text-xs text-gray-500">{contact.email}</div>}
    </div>
  )
}

/** Add one contact to a manual list (dynamic lists compute their own members). */
export function AddContactToListModal({ contact, onClose }: { contact: Contact; onClose: () => void }) {
  const { data: lists } = useLists()
  const manual = (lists ?? []).filter((l) => l.listType === 'manual')
  const add = useAddToList()
  const [listId, setListId] = useState('')
  const [done, setDone] = useState(false)
  const listName = manual.find((l) => l.id === listId)?.name
  return (
    <Modal
      title="Add to list"
      icon={<ListPlus size={17} className="text-brand-primary" />}
      onClose={onClose}
      footer={
        done || manual.length === 0 ? (
          <button type="button" onClick={onClose} className={done ? modalButton.primary : modalButton.ghost}>{done ? 'Done' : 'Close'}</button>
        ) : (
          <>
            <button type="button" onClick={onClose} className={modalButton.ghost}>Cancel</button>
            <button
              type="button"
              disabled={!listId || add.isPending}
              onClick={() => add.mutate({ listId, contactIds: [contact.id] }, { onSuccess: () => setDone(true) })}
              className={modalButton.primary}
            >
              {add.isPending ? 'Adding…' : 'Add to list'}
            </button>
          </>
        )
      }
    >
      <ContactBadge contact={contact} />
      {done ? (
        <p className="text-sm text-gray-700">Added to <b>{listName}</b>.</p>
      ) : manual.length === 0 ? (
        <p className="text-sm text-gray-600">
          There are no manual lists yet.{' '}
          <Link to="/lists" onClick={onClose} className="font-medium text-brand-primary hover:underline">Create one on the Lists page</Link>
        </p>
      ) : (
        <>
          <label className="block text-xs font-medium text-gray-500 mb-1">List</label>
          <Select size="md" value={listId} onChange={(e) => setListId(e.target.value)} className="w-full">
            <option value="">Choose a list…</option>
            {manual.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </Select>
          <p className="text-xs text-gray-500 mt-2">Only manual lists are shown — dynamic lists pick up contacts by their rules.</p>
          {add.isError && <p className="text-danger text-xs mt-2">{apiErrorMessage(add.error)}</p>}
        </>
      )}
    </Modal>
  )
}

/** Enrol one contact into a live sequence. */
export function AddContactToSequenceModal({ contact, onClose }: { contact: Contact; onClose: () => void }) {
  const { data: sequences } = useDripSequences()
  const live = (sequences ?? []).filter((s) => s.enabled)
  const enroll = useEnrollInDrip()
  const [sequenceId, setSequenceId] = useState('')
  const [enrolled, setEnrolled] = useState<number | null>(null)
  return (
    <Modal
      title="Add to sequence"
      icon={<Mail size={17} className="text-brand-primary" />}
      onClose={onClose}
      footer={
        enrolled != null || live.length === 0 ? (
          <button type="button" onClick={onClose} className={enrolled != null ? modalButton.primary : modalButton.ghost}>
            {enrolled != null ? 'Done' : 'Close'}
          </button>
        ) : (
          <>
            <button type="button" onClick={onClose} className={modalButton.ghost}>Cancel</button>
            <button
              type="button"
              disabled={!sequenceId || enroll.isPending}
              onClick={() =>
                enroll.mutate({ id: sequenceId, input: { contactIds: [contact.id] } }, { onSuccess: (r) => setEnrolled(r.enrolled) })
              }
              className={modalButton.primary}
            >
              {enroll.isPending ? 'Adding…' : 'Add to sequence'}
            </button>
          </>
        )
      }
    >
      <ContactBadge contact={contact} />
      {enrolled != null ? (
        <p className="text-sm text-gray-700">
          {enrolled === 0
            ? 'They’re already in that sequence, so nothing changed.'
            : 'Enrolled — the first email goes out on the sequence’s schedule.'}
        </p>
      ) : live.length === 0 ? (
        <p className="text-sm text-gray-600">
          There are no live sequences.{' '}
          <Link to="/drip" onClick={onClose} className="font-medium text-brand-primary hover:underline">Set one up on the Sequences page</Link>
        </p>
      ) : (
        <>
          <label className="block text-xs font-medium text-gray-500 mb-1">Sequence</label>
          <Select size="md" value={sequenceId} onChange={(e) => setSequenceId(e.target.value)} className="w-full">
            <option value="">Choose a sequence…</option>
            {live.map((s) => (
              <option key={s.id} value={s.id} disabled={s.steps.length === 0}>
                {s.name}
                {s.steps.length === 0 ? ' (no steps yet)' : ` · ${s.steps.length} email${s.steps.length === 1 ? '' : 's'}`}
              </option>
            ))}
          </Select>
          {enroll.isError && <p className="text-danger text-xs mt-2">{apiErrorMessage(enroll.error)}</p>}
        </>
      )}
    </Modal>
  )
}

export function DeleteContactModal({
  contact,
  onClose,
  onDeleted = onClose,
}: {
  contact: Contact
  onClose: () => void
  /** After a successful delete (defaults to onClose) — e.g. also close the contact that was open. */
  onDeleted?: () => void
}) {
  const del = useDeleteContact()
  return (
    <Modal
      title="Delete contact"
      tone="danger"
      icon={<AlertTriangle size={17} />}
      size="sm"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={modalButton.ghost}>Cancel</button>
          <button type="button" disabled={del.isPending} onClick={() => del.mutate(contact.id, { onSuccess: onDeleted })} className={modalButton.danger}>
            {del.isPending ? 'Deleting…' : 'Delete contact'}
          </button>
        </>
      }
    >
      <p className="text-sm text-gray-600">
        Delete <span className="font-semibold text-brand-darkest">{nameOf(contact)}</span>? Their timeline, list memberships and
        links to deals go too. The deals themselves stay. This can’t be undone.
      </p>
      {del.isError && <p className="text-danger text-xs mt-2">{apiErrorMessage(del.error)}</p>}
    </Modal>
  )
}
